import { InfiniteData } from '@tanstack/react-query';
import { AutomationStep, ParentStep } from '@/types/automationSteps.ts';
import { PaginatedResponse } from '@/types/common.ts';
import { StepRun } from '@/types/stepExecutions.ts';
import { getAllStepChildren, isStepWithChildren } from '@/utils/stepUtils.ts';

const ALPHABET = '23456789abcdefghjkmnpqrstuvwxyz';
const ID_LENGTH = 16; // total chars (excluding hyphens)

export function generateUid(): string {
  let raw = '';

  // Generate 16 random characters from the restricted alphabet
  for (let i = 0; i < ID_LENGTH; i++) {
    const randomIndex = Math.floor(Math.random() * ALPHABET.length);
    raw += ALPHABET[randomIndex];
  }

  // Chunk into xxxx-xxxx-xxxx-xxxx
  return raw.match(/.{1,4}/g)!.join('-');
}

export function capitalizeFirstLetter(str: string): string {
  if (str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function flattenInfiniteQueryData<T>(data?: InfiniteData<PaginatedResponse<T>>): T[] {
  return data ? data.pages.flatMap((page) => page.results) : [];
}

export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = Math.max(decimals, 0);
  const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

  return `${size} ${sizes[i]}`;
}

export function collectStepIds(steps: AutomationStep[]): Set<string> {
  const ids = new Set<string>();

  function traverse(stepList: AutomationStep[]) {
    for (const step of stepList) {
      ids.add(step.step_id);
      // Check if this step has children
      if (isStepWithChildren(step)) {
        const allChildren = getAllStepChildren(step);
        traverse(allChildren);
      }
    }
  }

  traverse(steps);
  return ids;
}

function isStepRun(p: StepRun | ParentStep): p is StepRun {
  return 'step_index' in p;
}

export function formatStepLabel(
  step: AutomationStep,
  index: number,
  parentSteps: (StepRun | ParentStep)[],
): string {
  // Build the numeric path from root → current
  const to1 = (n: number) => n + 1;
  const pathNumbers = parentSteps
    .map((p) => (isStepRun(p) ? to1(p.step_index) : to1(p.index)))
    .concat(to1(index));

  const path = pathNumbers.join('.');

  // Use name if present and non-empty after trim
  const rawName = (step as any)?.name;
  const name = typeof rawName === 'string' ? rawName.trim() : '';

  return `Step ${path}` + (name ? ` - ${name}` : '');
}
